// utils/flowUtils.js
import { v4 as uuidv4 } from 'uuid'; // Import UUID library

// Validates the structure of imported nodes
const validateNodes = (nodes) => {
  if (!Array.isArray(nodes)) {
    throw new Error('Nodes must be an array');
  }
  
  return nodes.every((node) => {
    const hasRequiredProps = 'id' in node && 'position' in node && 'data' in node;
    const hasValidPosition = typeof node.position?.x === 'number' && typeof node.position?.y === 'number';
    const hasValidData = typeof node.data === 'object' && node.data !== null;
    
    return hasRequiredProps && hasValidPosition && hasValidData;
  });
};

// Validates the structure of imported edges
const validateEdges = (edges) => {
  if (!Array.isArray(edges)) {
    throw new Error('Edges must be an array');
  }
  
  return edges.every((edge) => {
    const hasRequiredProps = 'id' in edge && 'source' in edge && 'target' in edge;
    return hasRequiredProps;
  });
};

// Exports the current flow state to a JSON file
export const exportFlow = (nodes, edges, canvasColor, gridState, minimapState, controlsState, viewport, isLocked) => {
  const flowData = {
    nodes,
    edges,
    settings: {
      canvasColor,
      gridState,
      minimapState,
      controlsState,
      isLocked,
    },
    viewport, // Add viewport to the exported JSON
    version: '1.0.0' // Adding version for future compatibility
  };
  
  const blob = new Blob([JSON.stringify(flowData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.href = url;
  link.download = `flow-diagram-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Imports and validates a flow diagram from a JSON file
export const importFlow = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const flowData = JSON.parse(event.target.result);
        
        if (!flowData.nodes || !flowData.edges || !flowData.settings) {
          throw new Error('Invalid flow data structure');
        }
        
        if (!validateNodes(flowData.nodes)) {
          throw new Error('Invalid node structure');
        }
        
        if (!validateEdges(flowData.edges)) {
          throw new Error('Invalid edge structure');
        }
        
        resolve(flowData);
      } catch (error) {
        reject(new Error(`Failed to parse flow data: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};


// Core function to apply flow data to the canvas
const applyFlowToCanvas = async (flowData, option, setNodes, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState) => {
  // Handle 'replace' option
  if (option === 'replace') {
    resetState(); // Reset existing API state
    setNodes([]); // Clear Existing Flow Nodes
    setEdges([]); // Clear Existing Flow Edges

    // A small delay to ensure reset completes before setting new state
    await new Promise(resolve => setTimeout(resolve, 50)); 
    
    // Filter out edges that connect to nodes with 'none' handles
    const validEdges = (flowData.edges || []).filter(edge => {
      const sourceNode = flowData.nodes.find(n => n.id === edge.source);
      const targetNode = flowData.nodes.find(n => n.id === edge.target);
      return sourceNode?.data?.sourcePosition !== 'none' && targetNode?.data?.targetPosition !== 'none';
    });
    
    setNodes(flowData.nodes || []); // Set new nodes
    setEdges(validEdges); // Set filtered edges
  } else if (option === 'add') {
    // Create a map to track old node IDs to new node IDs
    const idMap = {}; 
    // Map over nodes from the imported flow, generating new UUIDs
    const newNodes = flowData.nodes.map((nodeFromFile) => {
      const oldId = nodeFromFile.id;
      const newId = uuidv4(); // Generate a new unique ID for the node
      idMap[oldId] = newId; // Store the mapping from old ID to new ID
      return {
        ...nodeFromFile,
        id: newId, // Update the node with the new ID
      };
    });

    // Map over edges from the imported flow, updating source/target IDs
    const newEdges = flowData.edges.map((edgeFromFile) => ({
      ...edgeFromFile,
      id: uuidv4(), // Generate a new unique ID for the edge
      // Update source and target using the idMap, fall back to original if not found (should not happen in a valid flow)
      source: idMap[edgeFromFile.source] || edgeFromFile.source, 
      target: idMap[edgeFromFile.target] || edgeFromFile.target, 
    }));

    // Add the new nodes and edges to the existing ones
    setNodes((prevNodes) => [...prevNodes, ...newNodes]); 
    setEdges((prevEdges) => [...prevEdges, ...newEdges]);
  }

  // Apply settings from the flow data, with defaults
  setCanvasColor(flowData.settings?.canvasColor ?? '#111827');
  setGridState(flowData.settings?.gridState ?? true);
  setMinimapState(flowData.settings?.minimapState ?? false);
  setControlsState(flowData.settings?.controlsState ?? true);
  setIsLocked(flowData.settings?.isLocked ?? false);

  // Set viewport if defined in flow data, otherwise React Flow's default will be used
  if (flowData.viewport && option === 'replace') { // Only set viewport on replace to avoid jarring jumps
    setReactFlowViewport(flowData.viewport);
  }
  
  // Fit view after a short delay to ensure elements are rendered
  setTimeout(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({
        padding: 0.2, // Add some padding around the fitted view
        duration: 300, // Animate the fit view transition
        maxZoom: 1.15 // Set a maximum zoom level for fitView
      });
    }
  }, 300); // Delay to allow DOM updates
};

// Handles flow selection from a JSON file path (preset flows)
export const handleFlowSelect = async (flowPath, setNodes, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState) => {
  try {
    const response = await fetch(flowPath); // Fetch the flow data from the provided path
    const flowData = await response.json(); // Parse the JSON response

    // Call the common function to apply the flow, always replacing the current flow
    await applyFlowToCanvas(flowData, 'replace', setNodes, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState);
  } catch (error) {
    console.error('Error loading preset flow:', error);
    alert('Failed to load preset flow: ' + error.message); // Notify user of failure
  }
};

// Handles flow import from a user-selected file
export const handleImport = (file, option, nodes, setNodes, edges, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState) => {
  const reader = new FileReader(); // Create a new FileReader instance
  reader.onload = async (event) => { // Define what happens once the file is loaded
    try {
      const flowData = JSON.parse(event.target.result); // Parse the file content as JSON
      // Call the common function to apply the flow, using the specified option ('replace' or 'add')
      await applyFlowToCanvas(flowData, option, setNodes, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState);
    } catch (error) {
      console.error('Error importing flow from file:', error);
      alert('Failed to import flow from file: ' + error.message); // Notify user of failure
    }
  };
  reader.readAsText(file); // Start reading the file as text
};


// // Handles flow selection from a JSON file path
// export const handleFlowSelect = async (flowPath, setNodes, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState) => {
//   try {
//     // RESET FIRST
//     resetState();
//     setNodes([]);
//     setEdges([]);
    
//     await new Promise(resolve => setTimeout(resolve, 50));

//     // LOAD NEW FLOW
//     const response = await fetch(flowPath);
//     const flowData = await response.json();
//     setNodes(flowData.nodes || []);
//     setEdges(flowData.edges || []);
//     setCanvasColor(flowData.settings?.canvasColor || '#111827');
//     setGridState(flowData.settings?.gridState ?? true);
//     setMinimapState(flowData.settings?.minimapState ?? false);
//     setControlsState(flowData.settings?.controlsState ?? true);
//     setIsLocked(flowData.settings?.isLocked ?? false);

//     // Set the viewport if it exists in the flow data
//     if (flowData.viewport) {
//       setReactFlowViewport(flowData.viewport);
//     }

//     // Use setTimeout to ensure nodes are rendered before fitting
//     setTimeout(() => {
//       if (reactFlowInstance) {
//         reactFlowInstance.fitView({
//           padding: 0,
//           duration: 300,
//           maxZoom: 1.1
//         });
//       }
//     }, 300);
//   } catch (error) {
//     console.error('Error loading flow:', error);
//     alert('Failed to load flow: ' + error.message);
//   }
// };

// // Handles flow import from a file
// export const handleImport = (file, option, nodes, setNodes, edges, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState) => {
//   console.log("ENTERED handleImport");
//   const reader = new FileReader();
//   // reader.onload = (event) => {
//   reader.onload = async (event) => {
//     const flow = JSON.parse(event.target.result);
    
//     if (option === 'replace') {
//       resetState();
//       setNodes([]);
//       setEdges([]);

//       await new Promise(resolve => setTimeout(resolve, 50));
      
      
//       setNodes(flow.nodes || []);
//       setEdges(flow.edges || []);
//       console.log("nodes", flow.nodes);
//       console.log("edges", flow.edges);
//     } else if (option === 'add') {

//       const idMap = {};
//       // Add to the current flow and regenerate UUIDs for nodes
//       const newNodes = flow.nodes.map((nodeFromFile) => {
//         const oldId = nodeFromFile.id;
//         const newId = uuidv4(); // Regenerate UUID for each node
//         idMap[oldId] = newId;
//         return {
//           ...nodeFromFile,
//           id: newId,
//         };
//       });

//       // Regenerate UUIDs for edges to match the new node IDs
//       const newEdges = flow.edges.map((edgeFromFile) => ({
//         ...edgeFromFile,
//         id: uuidv4(), // Regenerate UUID for each edge
//         source: idMap[edgeFromFile.source] || edgeFromFile.source, // Use map to find new source ID
//         target: idMap[edgeFromFile.target] || edgeFromFile.target, // Use map to find new target ID
//       }));

//       setNodes((prevNodes) => [...prevNodes, ...newNodes]);
//       setEdges((prevEdges) => [...prevEdges, ...newEdges]);
//     }


//     // console.log("flow?.settings?.isLocked", flow?.settings?.isLocked);
//     // console.log("flow.settings.isLocked", flow.settings.isLocked);

//     setCanvasColor(flow?.settings?.canvasColor ?? '#111827');
//     setGridState(flow?.settings?.gridState ?? true);
//     setMinimapState(flow?.settings?.minimapState ?? false);
//     setControlsState(flow?.settings?.controlsState ?? true);
//     setIsLocked(flow?.settings?.isLocked ?? false); // Added default value
//     // setReactFlowViewport(flow?.viewport || { x: 0, y: 0, zoom: 1 });

//     setTimeout(() => {
//       if (reactFlowInstance) {
//         reactFlowInstance.fitView({
//           padding: 0.2,
//           duration: 300,
//           maxZoom: 1.15
//         });
//       }
//     }, 300);
//   };
//   reader.readAsText(file);
// };